// Sertica's Navigation tree (SPEC §9): the record at the root; under it its timeline, its
// linked RFQs, POs and requisitions, its documents and its response logs. Only records branch: a
// linked record opens in place with "+" (its own links, documents and responses, read from the
// store), never back to the record it hangs from, and its own links are leaves. A click on a
// record's name opens it as a page.
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { formatDate, formatFileSize } from "@po/shared";
import { api, type PoDetail, type ReqDetail, type RfqDetail } from "../lib/api";
import { spaced } from "../lib/format";
import type { DrawerRef } from "./Drawer";

type Kind = "requisition" | "rfq" | "po";
type Open = (r: DrawerRef) => void;
type Node = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  title?: string;
  /** Opens the node's record or file as a page. */
  onOpen?: () => void;
  children?: Node[];
  /** Children read when the node is first opened: a linked record's own links. */
  lazy?: { kind: Kind; no: string };
  open?: boolean;
  bold?: boolean;
  muted?: boolean;
};

const svg = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;
const ICON: Record<Kind | "doc" | "response", ReactNode> = {
  requisition: (
    <svg {...svg} className="text-brand-2">
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
      <path d="M2 3h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L21 7H6" />
    </svg>
  ),
  rfq: (
    <svg {...svg} className="text-brand-2">
      <circle cx="12" cy="12" r="9" />
      <path d="M14.8 9.2A3 3 0 0 0 12 8c-1.7 0-3 .9-3 2s1.3 1.7 3 2 3 .9 3 2-1.3 2-3 2a3 3 0 0 1-2.8-1.2M12 6v2M12 16v2" />
    </svg>
  ),
  po: (
    <svg {...svg} className="text-brand-2">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  ),
  doc: (
    <svg {...svg} className="text-muted">
      <path d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7L14 4.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4L15.5 7.5" />
    </svg>
  ),
  response: (
    <svg {...svg} className="text-muted">
      <path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l1-4.5A8 8 0 1 1 21 12z" />
    </svg>
  ),
};

/** "SO14260001R04 - 005664 - THOMAS MARITIM INDONESIA - Ordered", as Sertica names a record. */
const named = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" - ");

type Link = { no: string; status?: string | null; supplierNo?: string | null; supplier?: string | null; unitNo?: string | null; vessel?: string | null };
/** Where a group of nodes sits: the page's own record, or a linked record opened in place. */
type Ctx = { own: boolean; onOpen: Open; /** Under a linked record: the records the page already lists, left out of its links. */ shown?: ReadonlySet<string> };

const recordNode = (kind: Kind, x: Link, c: Ctx): Node => ({
  key: `${kind}:${x.no}`,
  icon: ICON[kind],
  label: kind === "requisition" ? named(x.no, x.unitNo, x.vessel, spaced(x.status)) : named(x.no, x.supplierNo, x.supplier, spaced(x.status)),
  onOpen: () => c.onOpen({ type: kind, no: x.no }),
  // Only the page's own links open in place; theirs are leaves.
  lazy: c.own ? { kind, no: x.no } : undefined,
});

/** The records of one group; under a linked record, those the page already lists are left out, so nothing shows twice. */
const records = (kind: Kind, xs: Link[], c: Ctx) => xs.filter((x) => !c.shown?.has(x.no)).map((x) => recordNode(kind, x, c));

/** A group; under a linked record, an empty or unread one is left out. */
const group = (key: string, label: string, children: Node[] | null, c: Ctx, empty = "None"): Node[] =>
  children?.length
    ? [{ key, label, open: true, children }]
    : c.own
      ? [{ key, label, open: true, children: [{ key: `${key}:none`, label: empty, muted: true }] }]
      : [];

function documents(kind: Kind, no: string, d: Pick<ReqDetail, "attachments" | "attachmentsFetchedAt">, c: Ctx, readBy: string): Node[] {
  const onOpen = c.onOpen;
  return group(
    `${kind}:${no}:docs`,
    "Documents",
    !d.attachmentsFetchedAt
      ? c.own
        ? [{ key: `${kind}:${no}:docs:unread`, label: `Not read from Sertica yet. ${readBy} to load the list.`, muted: true }]
        : null
      : d.attachments.map((a) => ({
          key: `${kind}:${no}:doc:${a.documentNo}`,
          icon: ICON.doc,
          label: (
            <>
              {a.fileName ?? `Document ${a.documentNo}`}
              {a.fileSize != null && <span className="text-muted"> · {formatFileSize(a.fileSize)}</span>}
              {/* Who uploaded it and when, as a response log line shows its sender. */}
              {a.uploadedBy && <span className="text-muted tnum"> · {a.uploadedBy}</span>}
              {a.uploadedAt && <span className="text-muted tnum"> · {when(a.uploadedAt)}</span>}
            </>
          ),
          title:
            [a.description && a.description !== a.fileName ? a.description : null, a.uploadedBy && `Uploaded by ${named(a.uploadedBy, a.uploadedByName)}`].filter(Boolean).join("\n") ||
            undefined,
          onOpen: () => onOpen({ type: "attachment", recordType: kind, recordNo: no, documentNo: a.documentNo, fileName: a.fileName }),
        })),
    c,
  );
}

function responses(kind: Kind, no: string, d: Pick<ReqDetail, "responses" | "responsesFetchedAt">, c: Ctx): Node[] {
  return group(
    `${kind}:${no}:responses`,
    "Response Logs",
    !d.responsesFetchedAt
      ? c.own
        ? [{ key: `${kind}:${no}:responses:unread`, label: "Not read from Sertica yet. Refresh from Sertica to load them.", muted: true }]
        : null
      : d.responses.map((x, i) => ({
          key: `${kind}:${no}:response:${i}`,
          icon: ICON.response,
          // One line for every response log, requisition or PO: the sender's user number, the time,
          // then the message's first line; the name and role (POs) on hover only.
          // The API has no recipient on these responses (Sertica 5.26.6.1). Time is server-local (WIB).
          label: (
            <>
              <span className="font-medium tnum">{x.userNo ?? "unknown user"}</span>
              {x.at && <span className="text-muted tnum"> · {when(x.at)}</span>}
              {x.text && <> - {x.text.split("\n")[0]}</>}
            </>
          ),
          title: [named(x.userNo, x.user, x.role && `as ${x.role}`), x.text].filter(Boolean).join("\n") || undefined,
          // "+" only when the label shows just the first line of a longer text.
          children: x.text && x.text.trim().includes("\n") ? [{ key: `${kind}:${no}:response:${i}:text`, label: <span className="whitespace-pre-line break-words">{x.text}</span> }] : undefined,
        })),
    c,
  );
}

type Event = ReqDetail["events"][number];

/**
 * Sertica time is server-local (WIB): the date and HH:MM. A date Sertica keeps without a time
 * (00:00, e.g. the superintendent approval date) shows the date alone, never a made-up time.
 */
const dateOnly = (at: string) => at.slice(11, 16) === "00:00";
const when = (at: string) => `${formatDate(at)}${dateOnly(at) ? "" : ` ${at.slice(11, 16)}`}`;

/**
 * A record's timeline, first under the record: who did each step and when (SPEC §9). Open on the
 * page's own record, closed on a linked one; a step not reached yet is greyed.
 */
function timeline(key: string, events: Event[], c: Ctx): Node {
  return {
    key,
    label: "Timeline",
    open: c.own,
    children: events.map((ev, i) => ({
      key: `${key}:${i}`,
      icon: (
        <span
          aria-hidden
          className="w-2.5 h-2.5 rounded-full border-2 inline-block"
          style={{ borderColor: "var(--brand)", background: ev.at ? "var(--brand)" : "transparent" }}
        />
      ),
      muted: !ev.at,
      title: ev.note ?? undefined,
      label: (
        <>
          {ev.label}
          <span className="tnum" title={ev.at && dateOnly(ev.at) ? "Sertica records the date only, not the time" : undefined}>
            {" "}
            · {ev.at ? when(ev.at) : "not yet"}
          </span>
          {/* The user number only; the name shows on hover. */}
          {ev.userNo && (
            <span className="text-ink2 tnum" title={ev.user ?? undefined}>
              {" "}
              · {ev.note?.endsWith(" by") ? `${ev.note} ` : ""}
              {ev.userNo}
            </span>
          )}
          {ev.note && !ev.note.endsWith(" by") && <span className="text-muted"> · {ev.note}</span>}
        </>
      ),
    })),
  };
}

/** What sits under a record, as in Sertica's tree, its stage timeline first. */
function childrenOf(kind: Kind, d: ReqDetail | PoDetail | RfqDetail, c: Ctx): Node[] {
  const readBy = kind === "rfq" ? "Search for it or open Calculate PO" : "Search for it or refresh from Sertica";
  if (kind === "requisition") {
    const r = d as ReqDetail;
    return [
      timeline(`req:${r.no}:timeline`, r.events, c),
      // Its RFQs and POs only on the requisition's own page: under an RFQ or PO they repeat that record's own.
      ...(c.own ? group(`req:${r.no}:rfqs`, "Request for Quotes", records("rfq", r.rfqs, c), c) : []),
      ...(c.own ? group(`req:${r.no}:pos`, "Purchase Orders", records("po", r.pos, c), c) : []),
      ...documents(kind, r.no, r, c, readBy),
      ...responses(kind, r.no, r, c),
    ];
  }
  if (kind === "po") {
    const p = d as PoDetail;
    return [
      timeline(`po:${p.no}:timeline`, p.events, c),
      // Its RFQs and requisitions only on the PO's own page: under a requisition or RFQ they repeat that record's own.
      ...(c.own ? group(`po:${p.no}:rfqs`, "Request for Quotes", records("rfq", p.rfqs, c), c) : []),
      ...(c.own ? group(`po:${p.no}:reqs`, "Requisitions", records("requisition", p.requisitions, c), c) : []),
      ...documents(kind, p.no, p, c, readBy),
      ...responses(kind, p.no, p, c),
    ];
  }
  // Sertica records no responses on an RFQ.
  const q = d as RfqDetail;
  return [
    timeline(`rfq:${q.no}:timeline`, q.events, c),
    ...group(`rfq:${q.no}:reqs`, "Requisitions", records("requisition", q.requisitions, c), c),
    ...group(`rfq:${q.no}:rfqs`, "Connected RFQs", records("rfq", q.rfqs, c), c),
    // Its POs only on the RFQ's own page: under a requisition or PO they repeat that record's own.
    ...(c.own ? group(`rfq:${q.no}:pos`, "Purchase Orders", records("po", q.pos, c), c) : []),
    ...documents(kind, q.no, q, c, readBy),
  ];
}

const PATH: Record<Kind, string> = { requisition: "/requisitions/", rfq: "/rfqs/", po: "/pos/" };

/** A linked record's own children, read from the store when it is first opened. */
function LazyChildren({ kind, no, shown, onOpen }: { kind: Kind; no: string; shown: ReadonlySet<string>; onOpen: Open }) {
  const path = `${PATH[kind]}${encodeURIComponent(no)}`;
  const { data, isLoading, isError } = useQuery({ queryKey: ["record", path], queryFn: () => api<ReqDetail | PoDetail | RfqDetail>(path) });
  if (isLoading) return <Leaf label="Loading…" />;
  if (isError || !data) return <Leaf label="Not in the store yet: open it to fetch it from Sertica." />;
  const nodes = childrenOf(kind, data, { own: false, onOpen, shown });
  if (!nodes.length) return <Leaf label="No other links, documents or responses" />;
  return (
    <ul>
      {nodes.map((n) => (
        <TreeNode key={n.key} n={n} onOpen={onOpen} shown={shown} />
      ))}
    </ul>
  );
}

const Leaf = ({ label }: { label: string }) => (
  <ul>
    <li className="pl-5 py-[2px] text-muted">{label}</li>
  </ul>
);

/** One row of the tree: Sertica's +/− box, the icon, the name; children under a dotted guide. */
function TreeNode({ n, onOpen, shown }: { n: Node; onOpen: Open; /** The records the page lists, left out of linked records' links. */ shown: ReadonlySet<string> }) {
  const [open, setOpen] = useState(!!n.open);
  const expandable = !!n.lazy || !!n.children?.length;
  return (
    <li className="relative">
      <div className={`flex items-center gap-1.5 py-[2px] min-w-0 ${n.muted ? "text-muted" : "text-ink"}`} title={n.title}>
        {expandable ? (
          <button
            className="w-[13px] h-[13px] shrink-0 inline-flex items-center justify-center border border-muted/60 bg-card text-[10px] leading-none text-ink2"
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => setOpen(!open)}
          >
            {open ? "−" : "+"}
          </button>
        ) : (
          <span className="w-[13px] shrink-0" aria-hidden />
        )}
        {n.icon && <span className="shrink-0 inline-flex items-center">{n.icon}</span>}
        {n.onOpen ? (
          <button className={`truncate text-left hover:underline hover:text-brand ${n.bold ? "font-bold" : ""}`} onClick={n.onOpen}>
            {n.label}
          </button>
        ) : (
          <span className={`truncate ${n.bold ? "font-bold" : ""}`}>{n.label}</span>
        )}
      </div>
      {open && expandable && (
        <div className="ml-[6px] pl-[13px] border-l border-dotted border-muted/60">
          {n.lazy ? (
            <LazyChildren kind={n.lazy.kind} no={n.lazy.no} shown={shown} onOpen={onOpen} />
          ) : (
            <ul>
              {n.children!.map((c) => (
                <TreeNode key={c.key} n={c} onOpen={onOpen} shown={shown} />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The page's Navigation panel: the record at the root, then its links, documents, response logs
 * and (requisition and PO) stage timeline, in place of separate sections.
 */
export function NavTree({ kind, record, onOpen }: { kind: Kind; record: ReqDetail | PoDetail | RfqDetail; onOpen: Open }) {
  const r = record as Partial<ReqDetail & PoDetail & RfqDetail>;
  const root: Node = {
    key: `${kind}:${record.no}:root`,
    icon: ICON[kind],
    bold: true,
    open: true,
    label:
      kind === "requisition"
        ? named(record.no, r.unitNo, r.vessel, spaced(r.status))
        : kind === "po"
          ? named(record.no, r.supplierNo, r.supplier, spaced(r.orderStatus))
          : named(record.no, r.supplierNo, r.supplier, spaced(r.status)),
    children: childrenOf(kind, record, { own: true, onOpen }),
  };
  // The page's record and every record it links to: a linked record opened in place lists only others.
  const shown = new Set([record.no, ...[...(r.requisitions ?? []), ...(r.rfqs ?? []), ...(r.pos ?? [])].map((x) => x.no)]);
  return (
    <section className="card overflow-hidden" aria-label="Navigation">
      <h3 className="px-3 py-1.5 text-[13px] font-medium text-white bg-[var(--brand-2)]">Navigation</h3>
      <ul className="px-3 py-2 text-[12.5px] overflow-x-auto">
        <TreeNode n={root} onOpen={onOpen} shown={shown} />
      </ul>
    </section>
  );
}
